import { useInterval } from 'ahooks';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Step } from 'react-joyride';
import styled, { css, useTheme } from 'styled-components';

import { MOBILE_TRADE_TABS, NOTIFICATION_TYPES } from 'src/common/constants';
import { isEmptyObj } from 'src/common/helpers';
import { Tab, Tabs, Notify } from 'src/components/core';
import { ExpandIn, ExpandOut } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { UserGuid } from 'src/components/core/user-guide';
import { NotLogin } from 'src/components/features/common/not-login';
import { RouteMeta } from 'src/components/features/common/route-meta/RouteMeta';
import { Assets } from 'src/components/features/derivative/assets-riskLimit';
import Chart from 'src/components/features/derivative/chart';
import { ChartHeader } from 'src/components/features/derivative/chart/ChartHeader';
import { ContractDetail } from 'src/components/features/derivative/contract-detail';
import { OrderBook } from 'src/components/features/derivative/order-book';
import { OrderFormMobile, OrderFormDesktop } from 'src/components/features/derivative/order-forms';
import { OrderTable } from 'src/components/features/derivative/order-table';
import { PairInfo } from 'src/components/features/derivative/pairInfo';
import { RecentTrade } from 'src/components/features/derivative/recent-trade';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { getUserAssets, getUserInviteCode } from 'src/pages/_redux/user/usersActions';
import withWebSocket from 'src/setup/socket/withWebSocket';
import { SocketWrapperProps } from 'src/types';

import {
    fetchAvailableAssets,
    fetchSymbolPositions,
    fetchSymbolSettings,
    getLeverage,
} from './_redux/derivativePageActions';
import {
    fetchWalletSubAccounts,
    fetchCoinTokens,
    fetchTokenRates,
    setSettings,
} from '../../pages/_redux/global/globalsActions';

const SideBar = styled.div`
    ${({ theme: { colors, breakpoints } }) => `
        width: 307px;
        min-height: 100vh;
        border-left: 1px solid ${colors.border.light};
        @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
            width: 25%;
        }
    `};
`;

const MainContent = styled.div`
    ${({ theme: { isMobile, breakpoints } }) => `
        @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
            width: calc(100% - 25%);
        }
        ${
            isMobile
                ? css`
                      width: 100%;
                  `
                : css`
                      width: calc(100% - 307px);
                  `
        }

        & #tv_chart_container {
            width: 100%;
            height: calc(100% - 32px);
        }
    `};
`;

const ChartMainContainer = styled(FlexContainer)<{
    isTradeExpand: boolean;
    isCharExpand: boolean;
}>`
    ${({ theme: { breakpoints }, isTradeExpand, isCharExpand }) => `
        height: ${isCharExpand ? '100%' : isTradeExpand ? '100%' : '55vh'};
        min-height: ${isTradeExpand ? '' : '632px'};
        @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
            width: 100%;
        }
    `};
`;

const ChartContainer = styled(FlexContainer)<{ isTradeExpand: boolean }>`
    ${({ theme: { colors, breakpoints }, isTradeExpand }) => `
        width: 100%;
        height: 100%;
        display: ${isTradeExpand ? 'none' : 'flex'};
        flex-direction: column;
        border-right: 1px solid ${colors.border.light};
        @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
            width: calc(100% - 30%);
        }
    `};
`;

const OrderContainer = styled(FlexContainer)<{
    isTradeExpand: boolean;
    isLargeScreen: boolean;
    isCharExpand: boolean;
}>`
    ${({ theme: { breakpoints }, isTradeExpand, isLargeScreen, isCharExpand }) => `
        flex-direction: ${!isLargeScreen ? 'column' : isTradeExpand ? 'column' : 'row'};
        height: 100%;
        width: ${isTradeExpand ? '100%' : isLargeScreen ? '614px' : '307px'};
        min-width: ${isTradeExpand ? '100%' : isLargeScreen ? '614px' : '307px'};
        display: ${isCharExpand ? 'none' : 'flex'};
        @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
            width: 30%;
            min-width: 30%;
        }
    `};
`;

const Container = styled.div`
    display: flex;
    overflow: hidden;
`;

const TabContainer = styled.div<{ isTradeExpand?: boolean }>`
    width: 100%;
    &:not(.order-book-tabs) {
        padding: 0 8px;
    }
    ${({ theme: { colors, typography, breakpoints }, isTradeExpand }) => `
        &.order-book-tabs {
            display: flex;
            @media (min-width: ${breakpoints.xxl}) {
                ${!isTradeExpand ? `display: none` : ``};
            }
            justify-content: space-between;
            align-items: center;
            padding-right: 16px;
            border-bottom: 1px solid ${colors.border.light};
            & > .tabs > .tab {
                padding: 11px 13px;
                margin: 0;
                font-size: ${typography.size.desktop.paragraph.p3};
                line-height: calc(${typography.lineHeight[2]} - 1px);
                &.tab-active {
                    color: ${colors.primary}
                }
            }
        }
        @media (max-width: ${breakpoints.md}) {
            border-bottom: 1px solid ${colors.border.light};
        }
    `};
    & .mobileTab {
        letter-spacing: -0.02em;
        padding: 10px 0;
    }
`;

const DerivativePage: FC<SocketWrapperProps> = () => {
    const { t } = useTranslation();
    const { isMobile, colors, breakpoints } = useTheme();
    const dispatch = useAppDispatch();

    const { isAuthenticated } = useAuth();

    const { activeOrders, toast, tvWidgetRef, currentSymbol, leverage } = useAppSelector(
        (state) => state.derivativePage,
    );

    const { activeCurrency, commonConfig } = useAppSelector((state) => state.globalData);

    const [activeTab, setActiveTab] = useState(0);
    const [activeChartTab, setActiveChartTab] = useState('chart');
    const [orderBookTab, setOrderBookTab] = useState(0);
    const [isCharExpand, setIsChartExpand] = useState(false);
    const [isTradeExpand, setIsTradeExpand] = useState(false);
    const [steps] = useState<Step[]>([
        {
            target: '.Transfer',
            content: 'Transfer funds to your Derivatives account',
            disableBeacon: true,
            floaterProps: {
                styles: { arrow: { spread: 9, length: 4 }, floater: { width: 170 } },
                placement: 'top-end',
            },
        },
        {
            target: '.PairInfo',
            content: 'Select the perpetual contract to trade',
            disableBeacon: true,
            floaterProps: { styles: { arrow: { spread: 9, length: 4 }, floater: { width: 170 } } },
            placement: 'bottom-start',
        },
        {
            target: '.OrderSide',
            content: 'Select order type and enter order details',
            disableBeacon: true,
            floaterProps: {
                styles: { arrow: { spread: 9, length: 4 }, floater: { width: 200 } },
                placement: 'top',
            },
        },
        {
            target: '.OrderBtn',
            content: 'Click on Open Long or Open Short to execute order',
            disableBeacon: true,
            floaterProps: { styles: { arrow: { spread: 9, length: 4 }, floater: { width: 200 } } },
        },
        {
            target: '.Position',
            content: 'Review your open position(s) and order history',
            disableBeacon: true,
            floaterProps: {
                styles: { arrow: { spread: 9, length: 4 }, floater: { width: 200 } },
                placement: 'top',
            },
        },
    ]);

    const dynamicMetaValues = useMemo(() => {
        return {
            symbolId: currentSymbol.symbolId,
            baseTokenName: currentSymbol.secondLevelUnderlyingName,
            quoteTokenName: currentSymbol.quoteTokenName,
            symbolName: currentSymbol.symbolName,
        };
    }, [currentSymbol]);

    const isLargeScreen = useMediaQuery(`(min-width: calc(${breakpoints.xxl}))`);

    const setExpandView = (isOrderBookExpand?: boolean) => {
        if (isOrderBookExpand !== undefined) {
            if (isOrderBookExpand) {
                setOrderBookTab(0);
            } else {
                setOrderBookTab(1);
            }
        }
        setIsTradeExpand(!isTradeExpand);
    };

    const fetchRates = useCallback(() => {
        if (!isEmptyObj(currentSymbol) && activeCurrency) {
            dispatch(
                fetchTokenRates({
                    tokens: currentSymbol.quoteTokenId,
                    legalCoins: `${activeCurrency},${currentSymbol.quoteTokenId}`,
                }),
            );
        }
    }, [currentSymbol, activeCurrency]);

    const hideUserGuid = () => {
        const settings = { ...commonConfig, hasReadDerivativesGuide: true };
        dispatch(setSettings({ common_config: JSON.stringify(settings) }));
    };

    useInterval(fetchRates, 3000);

    useEffect(() => {
        if (!isEmptyObj(toast)) {
            Notify({
                type: toast.type,
                title:
                    toast.values !== undefined
                        ? t(toast.title, { ...toast.values })
                        : t(toast.title),
                iconColor:
                    toast.type == NOTIFICATION_TYPES.SUCCESS
                        ? colors.alert.green
                        : toast.type == NOTIFICATION_TYPES.ERROR
                        ? colors.alert.red
                        : toast.type == NOTIFICATION_TYPES.INFO
                        ? colors.alert.purple
                        : toast.type == NOTIFICATION_TYPES.WARN
                        ? colors.alert.yellow
                        : colors.alert.blue,
                description:
                    toast.description &&
                    (toast.values !== undefined
                        ? t(toast.description, {
                              ...toast.values,
                              price: t(String(toast.values.price)).toLowerCase(),
                          })
                        : t(toast.description)),
            });
        }
    }, [toast, t]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchAvailableAssets());
            dispatch(getUserAssets({ unit: 'USDT' }));
        }
    }, [dispatch, isAuthenticated, activeOrders, leverage]);

    // fetch all settings related to current symbol
    useEffect(() => {
        if (isAuthenticated && currentSymbol.exchangeId) {
            dispatch(
                fetchSymbolSettings({
                    exchange_id: currentSymbol.exchangeId,
                    symbol_ids: currentSymbol.symbolId,
                }),
            );
            dispatch(
                fetchSymbolPositions({
                    exchange_id: currentSymbol.exchangeId,
                    symbol_id: currentSymbol.symbolId,
                }),
            );
            dispatch(getLeverage({ symbol_id: currentSymbol.symbolId }));
        }
    }, [dispatch, isAuthenticated, currentSymbol]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchWalletSubAccounts());
            dispatch(fetchCoinTokens());
            dispatch(getUserInviteCode());
        }
    }, [dispatch, isAuthenticated]);

    return useMemo(
        () => (
            <>
                <RouteMeta dynamicValues={dynamicMetaValues} />
                <PairInfo />
                <Container>
                    <MainContent>
                        {isMobile ? (
                            <>
                                <TabContainer>
                                    <Tabs value={activeTab} onChange={setActiveTab}>
                                        {Object.keys(MOBILE_TRADE_TABS).map(
                                            (tab: string, index) => {
                                                return (
                                                    <Tab
                                                        key={index}
                                                        label={t(MOBILE_TRADE_TABS[tab])}
                                                        value={index}
                                                        className={'mobileTab'}
                                                    />
                                                );
                                            },
                                        )}
                                    </Tabs>
                                </TabContainer>
                                <FlexContainer styles={{ minHeight: '321px', height: '50vh' }}>
                                    <Chart
                                        activeTab={activeTab}
                                        activeChart={activeChartTab}
                                        setActiveChart={setActiveChartTab}
                                    />
                                    {activeTab == 2 && <OrderBook />}
                                    {activeTab == 3 && <RecentTrade />}
                                </FlexContainer>
                            </>
                        ) : (
                            <ChartMainContainer
                                isCharExpand={isCharExpand}
                                isTradeExpand={isTradeExpand}
                            >
                                <ChartContainer isTradeExpand={isTradeExpand}>
                                    <ChartHeader
                                        activeTab={activeChartTab}
                                        setActiveTab={setActiveChartTab}
                                        isCharExpand={isCharExpand}
                                        setIsChartExpand={setIsChartExpand}
                                    />
                                    <Chart activeChart={activeChartTab} />
                                </ChartContainer>
                                <OrderContainer
                                    isCharExpand={isCharExpand}
                                    isLargeScreen={isLargeScreen}
                                    isTradeExpand={isTradeExpand}
                                >
                                    <TabContainer
                                        className={'order-book-tabs'}
                                        isTradeExpand={isTradeExpand}
                                    >
                                        <Tabs value={orderBookTab} onChange={setOrderBookTab}>
                                            <Tab key={0} label={t('order_book')} value={0} />
                                            <Tab key={1} label={t('recent_trades')} value={1} />
                                        </Tabs>
                                        {!isMobile ? (
                                            isTradeExpand ? (
                                                <ExpandIn
                                                    stroke={colors.text.tertiary}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setExpandView(undefined)}
                                                />
                                            ) : (
                                                <ExpandOut
                                                    stroke={colors.text.tertiary}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setExpandView(undefined)}
                                                />
                                            )
                                        ) : (
                                            ''
                                        )}
                                    </TabContainer>
                                    {isLargeScreen && !isTradeExpand && (
                                        <>
                                            <OrderBook
                                                isTradeExpand={isTradeExpand}
                                                setExpandView={() => setExpandView(true)}
                                            />
                                            <RecentTrade
                                                isTradeExpand={isTradeExpand}
                                                setExpandView={() => setExpandView(false)}
                                            />
                                        </>
                                    )}
                                    {isTradeExpand || !isLargeScreen ? (
                                        orderBookTab === 0 ? (
                                            <OrderBook isTradeExpand={isTradeExpand} />
                                        ) : (
                                            <RecentTrade isTradeExpand={isTradeExpand} />
                                        )
                                    ) : (
                                        <></>
                                    )}
                                </OrderContainer>
                            </ChartMainContainer>
                        )}
                        <OrderTable isExpand={isCharExpand ? true : isTradeExpand ? true : false} />
                    </MainContent>
                    {isMobile ? (
                        <OrderFormMobile />
                    ) : (
                        <SideBar>
                            <>
                                {isAuthenticated ? (
                                    <>
                                        <OrderFormDesktop />
                                        <Assets />
                                    </>
                                ) : (
                                    <NotLogin />
                                )}
                                <ContractDetail />
                            </>
                        </SideBar>
                    )}
                    {tvWidgetRef?.current._ready && isAuthenticated && (
                        <UserGuid
                            steps={steps}
                            showUserGuid={commonConfig?.hasReadDerivativesGuide}
                            onHide={hideUserGuid}
                        />
                    )}
                </Container>
            </>
        ),
        [
            activeChartTab,
            activeTab,
            orderBookTab,
            colors,
            isCharExpand,
            isAuthenticated,
            isMobile,
            isTradeExpand,
            commonConfig,
            steps,
            tvWidgetRef,
            setExpandView,
            isLargeScreen,
            t,
        ],
    );
};

export default withWebSocket(DerivativePage);
